import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  SimpleGrid,
  Card,
  CardBody,
  CardHeader,
  Icon,
  VStack,
  HStack,
  Button,
  useColorModeValue,
  Flex,
  Badge,
  Divider,
  Alert,
  AlertIcon,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Tooltip
} from '@chakra-ui/react';
import {
  AddIcon,
  SettingsIcon,
  ViewIcon,
  EditIcon,
  ChevronRightIcon,
  InfoIcon
} from '@chakra-ui/icons';
import { Link as RouterLink } from 'react-router-dom';
import { motion } from 'framer-motion';

const MotionBox = motion(Box);
const MotionCard = motion(Card);

const ContentTypes = () => {
  // Color mode values
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.600', 'gray.300');
  const accentColor = useColorModeValue('blue.500', 'blue.300');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  const contentTypeOptions = [
    {
      id: 'content-models',
      title: 'Content Models',
      description: 'Define the structure and fields for your content types. Create templates that determine what data your content can hold.',
      icon: SettingsIcon,
      color: 'blue',
      route: '/content-models',
      createRoute: '/content-models/create',
      features: [
        'Custom field types',
        'Validation rules',
        'Relationships',
        'Media attachments'
      ],
      stats: {
        total: '12',
        active: '10',
        draft: '2'
      }
    },
    {
      id: 'taxonomies',
      title: 'Taxonomies',
      description: 'Organize and categorize your content with custom taxonomies. Create tags, categories, and other classification systems.',
      icon: ViewIcon,
      color: 'green',
      route: '/taxonomies',
      createRoute: '/taxonomies/create',
      features: [
        'Hierarchical categories',
        'Multiple selection',
        'Custom metadata',
        'Bulk operations'
      ],
      stats: {
        total: '5',
        terms: '47',
        active: '5'
      }
    }
  ];

  return (
    <Box minH="100vh" bg={bgColor} py={8}>
      <Container maxW="container.xl">
        {/* Header */}
        <MotionBox
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          mb={8}
        >
          <Flex align="center" justify="space-between" mb={4}>
            <Box>
              <Heading size="xl" mb={2}>Content Types</Heading>
              <Text color={textColor} fontSize="lg">
                Manage the structure and organization of your content
              </Text>
            </Box>
            <Badge colorScheme="blue" fontSize="md" p={2} borderRadius="md">
              Structure
            </Badge>
          </Flex>

          {/* Info Alert */}
          <Alert status="info" borderRadius="md" mb={6}>
            <AlertIcon />
            <Box>
              <Text fontWeight="semibold">Getting Started</Text>
              <Text fontSize="sm">
                Content Models define the structure of your content, while Taxonomies help organize and categorize it. 
                Start by creating models for your content types, then add taxonomies for better organization.
              </Text>
            </Box>
          </Alert>
        </MotionBox>

        {/* Content Type Cards */}
        <MotionBox
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={8} mb={8}>
            {contentTypeOptions.map((option) => (
              <MotionCard
                key={option.id}
                variants={itemVariants}
                bg={cardBg}
                borderColor={borderColor}
                borderWidth="1px"
                shadow="lg"
                _hover={{
                  transform: 'translateY(-4px)',
                  shadow: 'xl'
                }}
                transition="all 0.3s"
                overflow="hidden"
              >
                <CardHeader pb={4}>
                  <Flex align="center" justify="space-between" mb={4}>
                    <HStack spacing={3}>
                      <Box
                        p={3}
                        borderRadius="lg"
                        bg={`${option.color}.500`}
                        color="white"
                      >
                        <Icon as={option.icon} boxSize={6} />
                      </Box>
                      <Box>
                        <Heading size="md" mb={1}>{option.title}</Heading>
                        <Badge colorScheme={option.color} variant="subtle">
                          {option.id === 'content-models' ? 'Structure' : 'Organization'}
                        </Badge>
                      </Box>
                    </HStack>
                  </Flex>
                  
                  <Text color={textColor} mb={4}>
                    {option.description}
                  </Text>

                  {/* Features */}
                  <Box mb={4}>
                    <Text fontSize="sm" fontWeight="semibold" mb={2} color={textColor}>
                      Key Features:
                    </Text>
                    <SimpleGrid columns={2} spacing={1}>
                      {option.features.map((feature, index) => (
                        <HStack key={index} spacing={2}>
                          <Box w={1} h={1} bg={`${option.color}.500`} borderRadius="full" />
                          <Text fontSize="sm" color={textColor}>{feature}</Text>
                        </HStack>
                      ))}
                    </SimpleGrid>
                  </Box>

                  {/* Stats */}
                  <Box mb={4}>
                    <Text fontSize="sm" fontWeight="semibold" mb={2} color={textColor}>
                      Statistics:
                    </Text>
                    <HStack spacing={4}>
                      {Object.entries(option.stats).map(([key, value]) => (
                        <Stat key={key} size="sm">
                          <StatNumber fontSize="lg" color={accentColor}>{value}</StatNumber>
                          <StatLabel fontSize="xs" textTransform="capitalize">{key}</StatLabel>
                        </Stat>
                      ))}
                    </HStack>
                  </Box>
                </CardHeader>

                <CardBody pt={0}>
                  <Divider mb={4} />
                  
                  {/* Action Buttons */}
                  <VStack spacing={3} align="stretch">
                    <Button
                      as={RouterLink}
                      to={option.route}
                      colorScheme={option.color}
                      size="lg"
                      rightIcon={<ChevronRightIcon />}
                      w="100%"
                    >
                      Manage {option.title}
                    </Button>
                    
                    <HStack spacing={2}>
                      <Button
                        as={RouterLink}
                        to={option.createRoute}
                        leftIcon={<AddIcon />}
                        variant="outline"
                        colorScheme={option.color}
                        size="md"
                        flex={1}
                      >
                        Create New
                      </Button>
                      <Tooltip label={`View all ${option.title.toLowerCase()}`}>
                        <Button
                          as={RouterLink}
                          to={option.route}
                          leftIcon={<ViewIcon />}
                          variant="ghost"
                          colorScheme={option.color}
                          size="md"
                          flex={1}
                        >
                          View All
                        </Button>
                      </Tooltip>
                    </HStack>
                  </VStack>
                </CardBody>
              </MotionCard>
            ))}
          </SimpleGrid>
        </MotionBox>

        {/* Quick Actions */}
        <MotionBox
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
            <CardHeader>
              <Flex align="center">
                <Icon as={InfoIcon} color="blue.500" mr={2} />
                <Heading size="md">Quick Actions</Heading>
              </Flex>
            </CardHeader>
            <CardBody>
              <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
                <Button
                  as={RouterLink}
                  to="/content-models/create"
                  leftIcon={<AddIcon />}
                  colorScheme="blue"
                  variant="outline"
                  size="lg"
                  h="auto"
                  py={4}
                  flexDirection="column"
                  _hover={{ bg: hoverBg }}
                >
                  <Text fontWeight="semibold" mb={1}>Create Content Model</Text>
                  <Text fontSize="sm" color={textColor} textAlign="center">
                    Define new content structure
                  </Text>
                </Button>

                <Button
                  as={RouterLink}
                  to="/taxonomies/create"
                  leftIcon={<AddIcon />}
                  colorScheme="green"
                  variant="outline"
                  size="lg"
                  h="auto"
                  py={4}
                  flexDirection="column"
                  _hover={{ bg: hoverBg }}
                >
                  <Text fontWeight="semibold" mb={1}>Create Taxonomy</Text>
                  <Text fontSize="sm" color={textColor} textAlign="center">
                    Add new classification system
                  </Text>
                </Button>

                <Button
                  as={RouterLink}
                  to="/content-models"
                  leftIcon={<ViewIcon />}
                  variant="outline"
                  size="lg"
                  h="auto"
                  py={4}
                  flexDirection="column"
                  _hover={{ bg: hoverBg }}
                >
                  <Text fontWeight="semibold" mb={1}>Browse Models</Text>
                  <Text fontSize="sm" color={textColor} textAlign="center">
                    View all content models
                  </Text>
                </Button>

                <Button
                  as={RouterLink}
                  to="/taxonomies"
                  leftIcon={<ViewIcon />}
                  variant="outline"
                  size="lg"
                  h="auto"
                  py={4}
                  flexDirection="column"
                  _hover={{ bg: hoverBg }}
                >
                  <Text fontWeight="semibold" mb={1}>Browse Taxonomies</Text>
                  <Text fontSize="sm" color={textColor} textAlign="center">
                    View all taxonomies
                  </Text>
                </Button>
              </SimpleGrid>
            </CardBody>
          </Card>
        </MotionBox>

        {/* Help Section */}
        <MotionBox
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          mt={8}
        >
          <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
            <CardBody>
              <Flex align="start" justify="space-between" direction={{ base: 'column', md: 'row' }}>
                <Box flex={1} pr={{ base: 0, md: 6 }} mb={{ base: 4, md: 0 }}>
                  <Heading size="sm" mb={2}>Need Help?</Heading>
                  <Text color={textColor} fontSize="sm" mb={4}>
                    Learn how to effectively structure your content and organize it with taxonomies.
                  </Text>
                  <VStack align="start" spacing={2}>
                    <Text fontSize="sm" color={textColor}>
                      • Content Models define what fields your content has
                    </Text>
                    <Text fontSize="sm" color={textColor}>
                      • Taxonomies help categorize and organize content
                    </Text>
                    <Text fontSize="sm" color={textColor}>
                      • Use relationships to connect different content types
                    </Text>
                  </VStack>
                </Box>
                <Button
                  as={RouterLink}
                  to="/help"
                  variant="outline"
                  colorScheme="blue"
                  size="sm"
                  flexShrink={0}
                >
                  View Documentation
                </Button>
              </Flex>
            </CardBody>
          </Card>
        </MotionBox>
      </Container>
    </Box>
  );
};

export default ContentTypes;